import { useCallback, useEffect, useMemo, useState } from 'react';
import { Search, X, Plus, Package, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { PAPER, INPUT_CLASS } from '../theme';

/**
 * MaterialsForm — payload shape (SD-1 §3):
 *
 *   {
 *     intent: 'quote' | 'order',
 *     supplier_id, supplier_name, supplier_first_name, supplier_email,
 *     delivery_by: 'YYYY-MM-DD' | '',
 *     line_items: [{
 *       description,     // required — "CLS Timber"
 *       width_mm,        // optional numeric
 *       depth_mm,        // optional numeric
 *       length_m,        // optional numeric (metres)
 *       qty,             // required numeric — 10
 *       unit,            // required — one of MATERIAL_UNITS
 *       notes,           // optional — free text
 *     }],
 *     extras: '',
 *     email_body_snapshot, email_subject_snapshot, email_sent_at,
 *   }
 *
 * The three dimension fields sit in one row (or stack neatly on mobile) and
 * are ALL optional — a user ordering plaster or plywood simply skips them.
 * Unit dropdown is required so the outgoing email can render "10 Lengths"
 * / "8 Bags" cleanly.
 */

const MATERIAL_UNITS = ['Lengths', 'm', 'Sheets', 'Bags', 'Items', 'Packs'];

const emptyLineItem = () => ({
  key: `li_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
  description: '',
  width_mm: '',
  depth_mm: '',
  length_m: '',
  qty: '',
  unit: 'Lengths',
  notes: '',
});

export const MaterialsForm = ({ value, onChange, readOnly = false }) => {
  const { api } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [loadingSuppliers, setLoadingSuppliers] = useState(true);

  const intent = value?.intent || 'quote';
  const supplier = useMemo(() => ({
    id: value?.supplier_id || null,
    name: value?.supplier_name || '',
    first_name: value?.supplier_first_name || '',
    email: value?.supplier_email || '',
  }), [value]);

  const lineItems = useMemo(() => {
    const items = value?.line_items;
    if (!items || items.length === 0) return [emptyLineItem()];
    // Ensure every item has a stable key + the SD-1 structured fields exist.
    return items.map((it, idx) => ({
      key: it.key || `li_${idx}`,
      description: '', width_mm: '', depth_mm: '', length_m: '', qty: '', unit: 'Lengths', notes: '',
      ...it,
    }));
  }, [value]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/diary/suppliers');
        setSuppliers(res.data || []);
      } catch (_e) {
        toast.error('Could not load suppliers');
      } finally {
        setLoadingSuppliers(false);
      }
    })();
  }, [api]);

  const setIntent = (next) => onChange({ ...(value || {}), intent: next });
  const setDeliveryBy = (v) => onChange({ ...(value || {}), delivery_by: v });
  const setExtras = (v) => onChange({ ...(value || {}), extras: v });

  const selectSupplier = (s) => {
    onChange({
      ...(value || {}),
      supplier_id: s.id,
      supplier_name: s.name,
      supplier_first_name: s.first_name || (s.name?.split(' ')[0] ?? ''),
      supplier_email: s.email || '',
    });
    setSupplierSearch('');
  };

  const clearSupplier = () => {
    onChange({
      ...(value || {}),
      supplier_id: null,
      supplier_name: '',
      supplier_first_name: '',
      supplier_email: '',
    });
  };

  const updateLineItem = (key, patch) => {
    const nextItems = lineItems.map(it => it.key === key ? { ...it, ...patch } : it);
    onChange({ ...(value || {}), line_items: nextItems });
  };

  const addLineItem = () => {
    onChange({ ...(value || {}), line_items: [...lineItems, emptyLineItem()] });
  };

  const removeLineItem = (key) => {
    const next = lineItems.filter(it => it.key !== key);
    onChange({ ...(value || {}), line_items: next.length ? next : [emptyLineItem()] });
  };

  const filteredSuppliers = useCallback(() => {
    const q = supplierSearch.trim().toLowerCase();
    if (!q) return suppliers;
    return suppliers.filter(s =>
      (s.name || '').toLowerCase().includes(q) ||
      (s.company || '').toLowerCase().includes(q) ||
      (s.trade || '').toLowerCase().includes(q)
    );
  }, [suppliers, supplierSearch]);

  return (
    <div className="space-y-3.5">
      {/* Intent toggle — SD-2 §4 colour coding: Enquiry=orange, Order=green.
          Internal DB value stays 'quote' for 'Enquiry' so we don't have to
          migrate historic entries; only the UI label + colour changed. */}
      <div>
        <label className="block text-[11px] uppercase tracking-wider font-semibold mb-1.5" style={{ color: PAPER.muted }}>
          Purpose
        </label>
        <div className="flex gap-2">
          {[
            { key: 'quote', label: 'Enquiry', hint: 'Ask for price, availability & lead time', bg: '#FFEDD5', border: '#FB923C', ink: '#9A3412', sub: '#B45309' },
            { key: 'order', label: 'Order',   hint: 'Please supply — no quote needed',        bg: '#DCFCE7', border: '#16A34A', ink: '#166534', sub: '#15803D' },
          ].map(({ key, label, hint, bg, border, ink, sub }) => {
            const active = intent === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setIntent(key)}
                className="flex-1 rounded-lg px-3 py-2.5 text-left transition-colors"
                style={{
                  background: active ? bg : PAPER.card,
                  border: `1px solid ${active ? border : PAPER.border}`,
                  color: active ? ink : PAPER.inkSoft,
                }}
                data-testid={`materials-intent-${key}`}
              >
                <div className="text-[13px] font-semibold">{label}</div>
                <div className="text-[11px] mt-0.5" style={{ color: active ? sub : PAPER.muted }}>{hint}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Supplier picker */}
      <div>
        <label className="block text-[11px] uppercase tracking-wider font-semibold mb-1.5" style={{ color: PAPER.muted }}>
          Supplier
        </label>

        {supplier.id ? (
          // Chosen supplier chip.
          <div
            className="rounded-lg p-3 flex items-center gap-2"
            style={{ background: PAPER.card, border: `1px solid ${PAPER.border}` }}
            data-testid="materials-selected-supplier"
          >
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: '#FFEDD5', color: '#C2410C' }}
            >
              <Package className="w-4 h-4" strokeWidth={2.25} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-semibold truncate" style={{ color: PAPER.ink }}>{supplier.name}</div>
              <div className="text-[11px] truncate" style={{ color: PAPER.muted }}>
                {supplier.email || 'no email on file — add one in Contacts'}
              </div>
            </div>
            <button
              type="button"
              onClick={clearSupplier}
              className="p-1.5 rounded hover:bg-black/5 transition-colors"
              style={{ color: PAPER.muted, display: readOnly ? 'none' : undefined }}
              title="Change supplier"
              data-testid="materials-clear-supplier"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          // Search + pick.
          <>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 w-4 h-4 pointer-events-none" style={{ color: PAPER.muted }} />
              <input
                type="text"
                value={supplierSearch}
                onChange={(e) => setSupplierSearch(e.target.value)}
                placeholder="Search suppliers"
                className={`${INPUT_CLASS} pl-9`}
                data-testid="materials-supplier-search"
              />
            </div>
            {loadingSuppliers ? (
              <div className="text-center text-[12px] py-3 mt-2" style={{ color: PAPER.muted }}>Loading…</div>
            ) : suppliers.length === 0 ? (
              <div
                className="mt-2 text-[12px] rounded-md px-3 py-3 flex items-start gap-2"
                style={{ background: PAPER.card, border: `1px dashed ${PAPER.border}`, color: PAPER.inkSoft }}
              >
                <HelpCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: PAPER.hint }} />
                <span>
                  No suppliers yet. Ask an admin to open a supplier in Contacts and tick
                  <b> &quot;Show in Site Diary&quot;</b> — then they&apos;ll appear here.
                </span>
              </div>
            ) : (
              <div
                className="mt-2 rounded-lg max-h-56 overflow-y-auto"
                style={{ background: PAPER.card, border: `1px solid ${PAPER.border}` }}
              >
                {filteredSuppliers().map(s => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => selectSupplier(s)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left text-[13px] transition-colors hover:bg-[#FAF7F2]"
                    style={{ borderBottom: `1px solid ${PAPER.divider}`, color: PAPER.ink }}
                    data-testid={`materials-supplier-option-${s.id}`}
                  >
                    <span className="flex-1 truncate">
                      {s.name}
                      {s.trade && <span style={{ color: PAPER.muted }}> · {s.trade}</span>}
                    </span>
                    <span className="text-[11px] truncate max-w-[40%]" style={{ color: PAPER.muted }}>
                      {s.email || ''}
                    </span>
                  </button>
                ))}
                {filteredSuppliers().length === 0 && (
                  <div className="text-center text-[12px] py-3" style={{ color: PAPER.muted }}>
                    No matches
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Delivery by */}
      <div>
        <label className="block text-[11px] uppercase tracking-wider font-semibold mb-1.5" style={{ color: PAPER.muted }}>
          Required by <span style={{ color: PAPER.hint }}>(optional)</span>
        </label>
        <input
          type="date"
          value={value?.delivery_by || ''}
          onChange={(e) => setDeliveryBy(e.target.value)}
          className={INPUT_CLASS}
          data-testid="materials-delivery-by"
        />
      </div>

      {/* Line items — new SD-1 structured shape */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: PAPER.muted }}>
            Items · {lineItems.length}
          </label>
          {!readOnly && (
          <button
            type="button"
            onClick={addLineItem}
            className="text-[12px] font-medium flex items-center gap-1 hover:underline"
            style={{ color: PAPER.accent }}
            data-testid="materials-add-line-item"
          >
            <Plus className="w-3.5 h-3.5" /> Add item
          </button>
          )}
        </div>
        <div className="space-y-2.5">
          {lineItems.map((item, idx) => (
            <LineItemBlock
              key={item.key}
              index={idx}
              item={item}
              onChange={(patch) => updateLineItem(item.key, patch)}
              onRemove={(!readOnly && lineItems.length > 1) ? () => removeLineItem(item.key) : null}
            />
          ))}
        </div>
      </div>

      {/* Extra notes for the email */}
      <div>
        <label className="block text-[11px] uppercase tracking-wider font-semibold mb-1.5" style={{ color: PAPER.muted }}>
          Anything else for the supplier? <span style={{ color: PAPER.hint }}>(optional)</span>
        </label>
        <textarea
          value={value?.extras || ''}
          onChange={(e) => setExtras(e.target.value)}
          placeholder="e.g. Delivery to north gate · site contact 07…"
          rows={2}
          className={INPUT_CLASS}
          data-testid="materials-extras"
        />
      </div>
    </div>
  );
};

// ---------- Per-line block ------------------------------------------------
// Mobile-first: three dim fields on one row using CSS grid — they stack in
// a comfortable 3-column layout on very narrow phones (still readable at
// 320px) and are all clearly labelled with their unit.
const LineItemBlock = ({ index, item, onChange, onRemove }) => {
  return (
    <div
      className="rounded-lg p-3 space-y-2.5"
      style={{ background: PAPER.card, border: `1px solid ${PAPER.border}` }}
      data-testid={`materials-line-item-${index}`}
    >
      <div className="flex items-center gap-2">
        <span className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: PAPER.muted }}>
          Item {index + 1}
        </span>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="ml-auto p-1 rounded hover:bg-red-50 transition-colors"
            style={{ color: PAPER.muted }}
            title="Remove"
            data-testid={`materials-remove-line-item-${index}`}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      <input
        type="text"
        value={item.description}
        onChange={(e) => onChange({ description: e.target.value })}
        placeholder="Description — e.g. CLS Timber"
        className={INPUT_CLASS}
        data-testid={`materials-line-description-${index}`}
      />

      {/* Dimensions row — W, D, L. Optional, mobile-friendly grid. */}
      <div>
        <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: PAPER.hint }}>
          Dimensions <span>(all optional)</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <DimField
            label="Width"
            unit="mm"
            value={item.width_mm}
            onChange={(v) => onChange({ width_mm: v })}
            testid={`materials-line-width-${index}`}
          />
          <DimField
            label="Depth"
            unit="mm"
            value={item.depth_mm}
            onChange={(v) => onChange({ depth_mm: v })}
            testid={`materials-line-depth-${index}`}
          />
          <DimField
            label="Length"
            unit="m"
            value={item.length_m}
            onChange={(v) => onChange({ length_m: v })}
            testid={`materials-line-length-${index}`}
          />
        </div>
      </div>

      {/* Qty + Unit — required pair sit on one line. */}
      <div className="flex gap-2">
        <div className="w-24">
          <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: PAPER.hint }}>Qty</div>
          <input
            type="text"
            inputMode="numeric"
            value={item.qty}
            onChange={(e) => onChange({ qty: e.target.value })}
            placeholder="10"
            className={INPUT_CLASS}
            data-testid={`materials-line-qty-${index}`}
          />
        </div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: PAPER.hint }}>Unit</div>
          <select
            value={item.unit}
            onChange={(e) => onChange({ unit: e.target.value })}
            className={INPUT_CLASS}
            data-testid={`materials-line-unit-${index}`}
          >
            {MATERIAL_UNITS.map(u => (
              <option key={u} value={u}>{u}</option>
            ))}
          </select>
        </div>
      </div>

      <input
        type="text"
        value={item.notes}
        onChange={(e) => onChange({ notes: e.target.value })}
        placeholder="Notes (optional) — e.g. kiln dried"
        className={INPUT_CLASS}
        data-testid={`materials-line-notes-${index}`}
      />
    </div>
  );
};

const DimField = ({ label, unit, value, onChange, testid }) => (
  <div>
    <div className="text-[10px] font-medium mb-1" style={{ color: PAPER.muted }}>
      {label} <span style={{ color: PAPER.hint }}>({unit})</span>
    </div>
    <input
      type="text"
      inputMode="decimal"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder="—"
      className={INPUT_CLASS}
      data-testid={testid}
    />
  </div>
);
